import {
  type Alcohol,
  type AromaIntensity,
  type Body,
  type Clarity,
  type Development,
  type Finish,
  type FiveStep,
  type Hue,
  type Mousse,
  type NoseCondition,
  type Quality,
  type QualityCriterion,
  type Readiness,
  type Sweetness,
  type VisualIntensity,
  type WineColour,
  HUES_BY_COLOUR,
  QUALITY,
} from "../scales.js";
import { type DescriptorId, isDescriptorId } from "../lexicon/aromas.js";

/**
 * The one note model every method renders from. Sections mirror how tasters
 * actually work (look → smell → taste → conclude) rather than any one
 * organisation's form; a WSET-style, CMS-grid-style or Dutch (SDEN) view is a
 * projection of this record, never a separate record.
 */
export const NOTE_SCHEMA = "proef.note/1" as const;

export interface Appearance {
  readonly clarity: Clarity;
  readonly intensity: VisualIntensity;
  readonly colour: WineColour;
  readonly hue: Hue;
  /** Bubbles, legs, sediment, rim variation — free text, optional. */
  readonly other?: string;
}

export interface Nose {
  readonly condition: NoseCondition;
  readonly intensity: AromaIntensity;
  readonly descriptors: readonly DescriptorId[];
  readonly development: Development;
}

export interface Palate {
  readonly sweetness: Sweetness;
  readonly acidity: FiveStep;
  /** Required for red wine, optional for rosé, absent for white. */
  readonly tannin?: FiveStep;
  readonly alcohol: Alcohol;
  readonly body: Body;
  /** Sparkling wine only. */
  readonly mousse?: Mousse;
  readonly flavourIntensity: FiveStep;
  readonly descriptors: readonly DescriptorId[];
  readonly finish: Finish;
}

export interface Conclusion {
  readonly quality: Quality;
  /** The criteria the taster can actually defend; drives `deriveQuality`. */
  readonly criteriaMet: readonly QualityCriterion[];
  readonly readiness: Readiness;
}

export interface StructuredNote {
  readonly schema: typeof NOTE_SCHEMA;
  readonly sparkling: boolean;
  readonly appearance: Appearance;
  readonly nose: Nose;
  readonly palate: Palate;
  readonly conclusion: Conclusion;
  /** The taster's own words, always allowed, never required. */
  readonly freeText?: string;
}

export interface NoteIssue {
  readonly path: string;
  readonly message: string;
}

/**
 * Structural validation beyond what a schema can express: cross-field rules
 * a tasting tutor would flag. Returns an empty array for a coherent note.
 */
export function validateNote(note: StructuredNote): NoteIssue[] {
  const issues: NoteIssue[] = [];
  const { appearance, nose, palate, conclusion } = note;

  if (!HUES_BY_COLOUR[appearance.colour].includes(appearance.hue)) {
    issues.push({
      path: "appearance.hue",
      message: `${appearance.hue} is not a hue of a ${appearance.colour} wine`,
    });
  }
  if (appearance.colour === "red" && palate.tannin === undefined) {
    issues.push({ path: "palate.tannin", message: "red wine needs a tannin assessment" });
  }
  if (appearance.colour === "white" && palate.tannin !== undefined) {
    issues.push({ path: "palate.tannin", message: "white wine is not assessed for tannin" });
  }
  if (note.sparkling && palate.mousse === undefined) {
    issues.push({ path: "palate.mousse", message: "sparkling wine needs a mousse assessment" });
  }
  if (!note.sparkling && palate.mousse !== undefined) {
    issues.push({ path: "palate.mousse", message: "mousse only applies to sparkling wine" });
  }
  for (const [section, ids] of [
    ["nose", nose.descriptors],
    ["palate", palate.descriptors],
  ] as const) {
    ids.forEach((id: string, i) => {
      if (!isDescriptorId(id)) {
        issues.push({
          path: `${section}.descriptors[${String(i)}]`,
          message: `unknown descriptor ${id}`,
        });
      }
    });
  }
  if (nose.condition === "unclean" && conclusion.quality !== "faulty") {
    issues.push({
      path: "conclusion.quality",
      message: "an unclean nose is a fault; quality must be faulty",
    });
  }
  const derived = deriveQuality(conclusion.criteriaMet, nose.condition === "unclean");
  if (
    conclusion.quality !== "faulty" &&
    QUALITY.indexOf(conclusion.quality) > QUALITY.indexOf(derived)
  ) {
    issues.push({
      path: "conclusion.quality",
      message: `${conclusion.quality} needs more defended criteria than ${String(conclusion.criteriaMet.length)} (supports at most ${derived})`,
    });
  }
  return issues;
}

/**
 * Quality follows from the number of criteria the taster can defend: none →
 * poor, one → acceptable, two → good, three → very good, all four →
 * outstanding. A fault overrides everything. This is the argument structure
 * shared by WSET-style and Dutch tasting education, expressed as a function so
 * the app can coach ("you ticked balance and length — that argues for good").
 */
export function deriveQuality(criteriaMet: readonly QualityCriterion[], faulty: boolean): Quality {
  if (faulty) return "faulty";
  const n = new Set(criteriaMet).size;
  return QUALITY[Math.min(n, 4) + 1] ?? "poor";
}
